<script setup lang="ts">
import { computed } from 'vue';
import {
  Input, InputNumber, Select, DatePicker, Button,
} from 'ant-design-vue';
import type { FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';

import type { UiSearchBaseInputEntry } from './types';
import { defaultOperatorsByType, type SearchOperatorItem } from './operators';

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type Any = any;

interface SelectOption {
  label: string;
  value: string | number | null;
}

const MULTI_SELECT_TYPES = ['select-many', 'select-menu', 'select-menu-many'];

const props = defineProps<{
  entry: UiSearchBaseInputEntry;
  rowClass?: string;
}>();

defineEmits<{
  remove: [];
}>();

const modelValue = defineModel<Any>('value');
const operatorValue = defineModel<string | undefined>('operator');

interface EntryView {
  name: string;
  type: keyof typeof defaultOperatorsByType;
  label: string;
  icon?: FontAwesomeIconProps['icon'];
  alwaysOn?: boolean;
  placeholder?: string;
  inputDisabled?: boolean;
  readOnlyOperator?: boolean;
  operators?: SearchOperatorItem[];
  selectItems?: SelectOption[];
  selectLoading?: boolean;
  selectDisabled?: boolean;
  selectSearchable?: boolean;
  selectPlaceholder?: string;
}

const view = computed<EntryView>(() => {
  const entry = props.entry as unknown as {
    name: string;
    type: EntryView['type'];
    field?: { label?: string };
    icon?: FontAwesomeIconProps['icon'];
    alwaysOn?: boolean;
    readOnlyOperator?: boolean;
    input?: { placeholder?: string; disabled?: boolean };
    operators?: SearchOperatorItem[];
    select?: {
      items?: SelectOption[];
      loading?: boolean;
      disabled?: boolean;
      searchable?: boolean;
      placeholder?: string;
    };
  };
  return {
    name: entry.name,
    type: entry.type,
    label: entry.field?.label ?? 'Unknown',
    icon: entry.icon,
    alwaysOn: entry.alwaysOn,
    placeholder: entry.input?.placeholder,
    inputDisabled: entry.input?.disabled,
    readOnlyOperator: entry.readOnlyOperator,
    operators: entry.operators,
    selectItems: entry.select?.items,
    selectLoading: entry.select?.loading,
    selectDisabled: entry.select?.disabled,
    selectSearchable: entry.select?.searchable,
    selectPlaceholder: entry.select?.placeholder,
  };
});

const operatorOptions = computed<SearchOperatorItem[]>(
  () => view.value.operators ?? defaultOperatorsByType[view.value.type] ?? [],
);

const onText = (value: string): void => {
  modelValue.value = value === '' ? undefined : value;
};

const onNumber = (value: number | string | null): void => {
  modelValue.value = value === null || value === '' ? undefined : value;
};

const onTags = (value: unknown): void => {
  const tags = Array.isArray(value) ? value : [];
  modelValue.value = tags.length ? tags : undefined;
};

const onDate = (value: string): void => {
  modelValue.value = value || undefined;
};

const onSelectMany = (value: unknown): void => {
  const selected = Array.isArray(value) ? value : [];
  modelValue.value = selected.length ? selected : undefined;
};

const onSelectSingle = (value: unknown): void => {
  modelValue.value = value === undefined || value === null || value === '' ? undefined : value;
};
</script>

<template>
  <div :class="rowClass">
    <div class="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700">
      <FaIcon v-if="view.icon" :icon="view.icon" />
      <span>{{ view.label }}</span>
    </div>
    <div class="flex items-stretch gap-2">
      <Select
        :value="operatorValue"
        :options="operatorOptions"
        :disabled="view.readOnlyOperator"
        class="w-40 shrink-0"
        :aria-label="`${view.label} operator`"
        @update:value="(value) => { operatorValue = value as string; }"
      />
      <div class="min-w-0 flex-1">
        <Input
          v-if="view.type === 'input'"
          :value="modelValue"
          :placeholder="view.placeholder"
          :name="view.name"
          :disabled="view.inputDisabled"
          allow-clear
          @update:value="onText"
        />
        <InputNumber
          v-else-if="view.type === 'number'"
          :value="modelValue"
          :placeholder="view.placeholder"
          :name="view.name"
          :disabled="view.inputDisabled"
          :controls="false"
          class="w-full"
          @update:value="onNumber"
        />
        <Select
          v-else-if="view.type === 'tags'"
          :value="modelValue"
          mode="tags"
          :open="false"
          :token-separators="[',']"
          :placeholder="view.placeholder"
          :disabled="view.inputDisabled"
          class="w-full"
          @update:value="onTags"
        />
        <DatePicker
          v-else-if="view.type === 'calendar'"
          :value="modelValue"
          value-format="YYYY-MM-DD"
          :placeholder="view.placeholder"
          :disabled="view.inputDisabled"
          class="w-full"
          @update:value="(value) => onDate(value as string)"
        />
        <Select
          v-else-if="MULTI_SELECT_TYPES.includes(view.type)"
          :value="modelValue"
          mode="multiple"
          :options="view.selectItems"
          :loading="view.selectLoading"
          :disabled="view.selectDisabled"
          :show-search="view.selectSearchable"
          option-filter-prop="label"
          :placeholder="view.selectPlaceholder"
          class="w-full"
          @update:value="onSelectMany"
        />
        <Select
          v-else-if="view.type === 'select'"
          :value="modelValue"
          :options="view.selectItems"
          :loading="view.selectLoading"
          :disabled="view.selectDisabled"
          :show-search="view.selectSearchable"
          option-filter-prop="label"
          :placeholder="view.selectPlaceholder"
          allow-clear
          class="w-full"
          @update:value="onSelectSingle"
        />
        <slot
          v-else-if="view.type === 'generic'"
          :name="`${view.name}-input`"
          :input-name="view.name"
          :value="modelValue"
        />
        <Input
          v-else
          :value="modelValue"
          :placeholder="view.placeholder"
          :name="view.name"
          :disabled="view.inputDisabled"
          allow-clear
          @update:value="onText"
        />
      </div>
      <Button
        v-if="!view.alwaysOn"
        :aria-label="`Remove ${view.label} filter`"
        class="shrink-0"
        @click="$emit('remove')"
      >
        <FaIcon :icon="['fal', 'xmark']" />
      </Button>
    </div>
  </div>
</template>
